import { when } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { IObject } from '@feathr/rachis';

import type { IRedirectDomainOption } from '.';

interface IProps {
  label?: string;
  onSelectSingle: (option: IRedirectDomainOption) => void;
  selectedDomainId?: string;
}

/**
 * Select component for content serving domains (Domain model).
 */
function ContentServingDomainSelect({
  label,
  onSelectSingle,
  selectedDomainId,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Domains } = useStore();
  const [domainOptions, setDomainOptions] = useState<IRedirectDomainOption[] | undefined>(
    undefined,
  );

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: readonly IRedirectDomainOption[]) => void): void => {
      async function fetchDomains(): Promise<void> {
        const filters: IObject = {};
        // Prevent get url from changing if there is no input to filter by
        if (inputValue.length) {
          filters.content_domain__icontains = inputValue;
        }
        const domains = Domains.list({ filters });
        await when(() => !domains.isPending);

        // Only include domains with verified content domains
        const readyDomainOptions: IRedirectDomainOption[] = domains.models
          .filter((domain) => domain.isContentServingReady)
          .map((domain) => ({
            name: domain.get('content_domain'),
            id: domain.id,
          }));

        // Set domain options on the first render to retrieve current value.
        if (domainOptions === undefined) {
          setDomainOptions(readyDomainOptions);
        }

        callback(readyDomainOptions);
      }

      void fetchDomains();
    },
    [Domains, domainOptions],
  );

  function getOptionLabel({ name: label }: IRedirectDomainOption): string {
    return label;
  }

  function getOptionValue({ id: value }: IRedirectDomainOption): string {
    return value;
  }

  return (
    <UniversalSelect
      async={true}
      defaultOptions={true}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      label={label ?? t('Content serving domain')}
      loadOptions={loadOptions}
      name={'content-serving-domain-select'}
      onSelectSingle={onSelectSingle}
      value={domainOptions?.find((option) => option.id === selectedDomainId)}
    />
  );
}

export default ContentServingDomainSelect;
